import React, {type ReactNode, useState} from 'react';

import TabbedModal from 'app/components/general/TabbedModal';
import {gt} from 'app/utils/i18n';

type EditContextType<T> = {
  setEditingProps: (
    props: Omit<KBGenericFormProps<T>, 'inPopover'> | null
  ) => Promise<any>;
  editingProps: KBGenericFormProps<T> | null;
};

export const KBEditItemContext = React.createContext<EditContextType<any>>({
  setEditingProps: () => Promise.resolve(true),
  editingProps: null,
});

type KBTreeEditItemContextProviderProps<T> = {
  children: ReactNode;
  FormComponent: React.ComponentType<KBGenericFormProps<T>>;
};

export const KBTreeEditItemContextProvider = <T,>({
  children,
  FormComponent,
}: KBTreeEditItemContextProviderProps<T>) => {
  const [editingProps, setEditingProps] =
    useState<KBGenericFormProps<T> | null>(null);

  const setEditing = (props: KBGenericFormProps<T> | null) =>
    Promise.resolve(setEditingProps(props));

  return (
    <KBEditItemContext.Provider
      value={{setEditingProps: setEditing, editingProps}}
    >
      {children}
      {editingProps != null && (
        <KBTreeEditItemModal
          item={editingProps.item}
          onClose={() => setEditingProps(null)}
          inPopover={true}
          FormComponent={FormComponent}
        />
      )}
    </KBEditItemContext.Provider>
  );
};

type KBGenericFormProps<T> = {
  item: T;
  parent?: Partial<T>;
  onClose: () => unknown;
  inPopover: boolean;
};

type KBTreeEditItemModalProps<T> = {
  FormComponent: React.ComponentType<KBGenericFormProps<T>>;
} & KBGenericFormProps<T>;

export const KBTreeEditItemModal = <T,>({
  FormComponent,
  item,
  parent,
  onClose,
}: KBTreeEditItemModalProps<T>) => {
  if (item == null) return null;

  return (
    <TabbedModal
      show={item != null}
      onCancel={onClose}
      tabs={[
        {
          eventKey: 'edit-object',
          name: gt('Edit'),
          icon: 'far fa-edit',
          content: (
            <FormComponent
              onClose={onClose}
              item={item}
              inPopover={true}
              parent={parent}
            />
          ),
        },
      ]}
    />
  );
};
